import {
  PropertyDocumentSection,
  PropertyDocumentSectionFileUi,
  PropertyDocumentSectionUi,
} from './property-list.models';

let documentSectionClientIdSequence = 0;
let documentFileClientIdSequence = 0;

export interface PropertyDocumentSectionValidationResult {
  title: string;
  files: PropertyDocumentSectionUi['files'];
  isValid: boolean;
  validationErrors: {
    titleRequired: boolean;
    filesRequired: boolean;
  };
}

export interface PropertyDocumentSectionsValidationResult {
  sections: PropertyDocumentSectionValidationResult[];
  hasSections: boolean;
  hasAnyValidSection: boolean;
  isValid: boolean;
}

export type PropertyDocumentSectionFileInit =
  Partial<PropertyDocumentSection['files'][number]> &
  Pick<PropertyDocumentSection['files'][number], 'fileName' | 'url' | 'fileType' | 'uploadedAt'>;

export type PropertyDocumentSectionInit =
  Partial<Omit<PropertyDocumentSectionUi, 'clientId' | 'files'>> &
  Partial<Pick<PropertyDocumentSection, 'clientId'>> & {
    title?: string;
    files?: PropertyDocumentSectionFileInit[];
  };

export function createEmptyPropertyDocumentSection(
  overrides: PropertyDocumentSectionInit = {}
): PropertyDocumentSectionUi {
  const {
    clientId,
    files,
    title,
    ...restOverrides
  } = overrides;

  return {
    clientId: clientId || createPropertyDocumentSectionClientId(),
    title: title ?? '',
    files: normalizePropertyDocumentSectionFiles(files || []),
    isPersisted: false,
    titleTouched: false,
    filesTouched: false,
    uploadingCount: 0,
    uploadingFileNames: [],
    validationErrors: {
      titleRequired: false,
      filesRequired: false,
    },
    ...restOverrides,
  };
}

export function normalizePropertyDocumentSection(
  section: PropertyDocumentSectionInit = {}
): PropertyDocumentSectionUi {
  const {
    clientId,
    files,
    title,
    ...restOverrides
  } = section;

  return {
    clientId: clientId || createPropertyDocumentSectionClientId(),
    title: title ?? '',
    files: normalizePropertyDocumentSectionFiles(files || []),
    isPersisted: false,
    titleTouched: false,
    filesTouched: false,
    uploadingCount: 0,
    uploadingFileNames: [],
    validationErrors: {
      titleRequired: false,
      filesRequired: false,
    },
    ...restOverrides,
  };
}

export function createPropertyDocumentSectionClientId(): string {
  documentSectionClientIdSequence += 1;
  return `document-section-${documentSectionClientIdSequence}`;
}

export function createPropertyDocumentFileClientId(): string {
  documentFileClientIdSequence += 1;
  return `document-file-${documentFileClientIdSequence}`;
}

export function normalizePropertyDocumentSectionFile(
  file: PropertyDocumentSectionFileInit
): PropertyDocumentSectionFileUi {
  return {
    ...file,
    clientId: file.clientId || createPropertyDocumentFileClientId(),
  };
}

export function normalizePropertyDocumentSectionFiles(
  files: PropertyDocumentSectionFileInit[]
): PropertyDocumentSectionFileUi[] {
  return files.map(normalizePropertyDocumentSectionFile);
}

export function validatePropertyDocumentSection(
  section: PropertyDocumentSectionUi
): PropertyDocumentSectionValidationResult {
  const title = typeof section?.title === 'string' ? section.title.trim() : '';
  const files = Array.isArray(section?.files) ? section.files : [];
  const validationErrors = {
    titleRequired: !title,
    filesRequired: files.length === 0,
  };

  return {
    title,
    files,
    isValid: !validationErrors.titleRequired && !validationErrors.filesRequired,
    validationErrors,
  };
}

export function validatePropertyDocumentSections(
  sections: PropertyDocumentSectionUi[]
): PropertyDocumentSectionsValidationResult {
  const validatedSections = (sections || []).map(validatePropertyDocumentSection);
  const hasSections = validatedSections.length > 0;
  const hasAnyValidSection = validatedSections.some(section => section.isValid);
  const isValid = hasSections && validatedSections.every(section => section.isValid);

  return {
    sections: validatedSections,
    hasSections,
    hasAnyValidSection,
    isValid,
  };
}

export function toSavablePropertyDocumentSections(
  sections: PropertyDocumentSectionUi[]
): PropertyDocumentSection[] {
  const validated = validatePropertyDocumentSections(sections);
  return validated.sections.map((validatedSection, index) => {
    const original = sections[index];
    return {
      _id: original?._id,
      title: validatedSection.title,
      files: validatedSection.files.map(file => ({
        fileName: file.fileName,
        url: file.url,
        fileType: file.fileType,
        uploadedAt: file.uploadedAt,
        s3Key: file.s3Key,
        sourceS3Key: file.sourceS3Key,
      })),
      originSectionId: original?.originSectionId,
      originPropertyId: original?.originPropertyId,
      bulkGroupId: original?.bulkGroupId,
      clonedFromSectionId: original?.clonedFromSectionId,
    };
  });
}
